import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { useId, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { trackTrialSignup } from "@/lib/analytics";
import { collectMetaMatchFromBrowser } from "@/lib/metaMatch";
import { trpcClient } from "@/lib/trpc";
import { cn } from "@/lib/utils";
import { Field, FieldError, FieldGroup, FieldLabel } from "./ui/field";

// Zod validation schema
const createFirstTeamFormSchema = z.object({
    name: z.string().min(1, "Team name is required").min(4, "Team name must be at least 4 characters long"),
});

type CreateFirstTeamFormData = z.infer<typeof createFirstTeamFormSchema>;

interface CreateFirstTeamFormProps {
    onSuccess?: () => void;
    className?: string;
}

export function CreateFirstTeamForm({
    onSuccess,
    className,
    ...props
}: CreateFirstTeamFormProps & React.ComponentProps<"div">) {
    const [isLoading, setIsLoading] = useState(false);
    const createOrganizationMutation = useMutation({
        mutationFn: (data: {
            name: string;
            meta?: { marketingConsent?: boolean; fbp?: string; fbc?: string; eventSourceUrl?: string };
        }) => trpcClient.auth.createOrganization.mutate(data),
    });

    const {
        register,
        handleSubmit,
        setError,
        formState: { errors },
    } = useForm({
        resolver: zodResolver(createFirstTeamFormSchema),
        mode: "onBlur",
    });

    const onSubmit = async (data: CreateFirstTeamFormData) => {
        try {
            setIsLoading(true);
            const match = collectMetaMatchFromBrowser();
            const result = await createOrganizationMutation.mutateAsync({
                name: data.name,
                ...(match
                    ? {
                          meta: {
                              marketingConsent: true,
                              ...(match.fbp ? { fbp: match.fbp } : {}),
                              ...(match.fbc ? { fbc: match.fbc } : {}),
                              eventSourceUrl: window.location.href,
                          },
                      }
                    : {}),
            });

            // Conversion tracking: a trial started (organization created).
            trackTrialSignup({
                organizationId: result?.organization?.id,
                method: "email",
            });

            window.location.href = "/";
        } catch (error: any) {
            setIsLoading(false);
            setError("name", { message: error.message });
        }
    };

    const id = useId();
    return (
        <div className={cn("flex flex-col gap-6", className)} {...props}>
            <Card>
                <CardHeader className="text-center">
                    <CardTitle className="text-xl">Set up your team</CardTitle>
                    <CardDescription>
                        You're not a member of any team yet. Create one to start collaborating.
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <form onSubmit={handleSubmit(onSubmit)} id={id}>
                        <div className="grid gap-6">
                            {errors.name && (
                                <div className=" text-red-600 bg-red-50 border border-red-200 rounded-md p-3">
                                    {errors.name.message}
                                </div>
                            )}

                            <FieldGroup className="gap-4">
                                <Field>
                                    <FieldLabel htmlFor="name">Team Name</FieldLabel>
                                    <Input
                                        type="text"
                                        placeholder="Acme Inc."
                                        autoFocus
                                        disabled={createOrganizationMutation.isPending || isLoading}
                                        {...register("name")}
                                        className={errors.name ? "border-red-500" : ""}
                                    />
                                    <FieldError errors={[errors.name]} />
                                </Field>
                            </FieldGroup>

                            <Button
                                type="submit"
                                form={id}
                                className="w-full text-base"
                                disabled={createOrganizationMutation.isPending || isLoading}
                            >
                                {createOrganizationMutation.isPending || isLoading ? "Creating team..." : "Create team"}
                            </Button>
                        </div>
                    </form>
                </CardContent>
            </Card>
        </div>
    );
}
